import React, { useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Tooltip,
} from "@mui/material";
import { Send } from "@mui/icons-material";
import { useBillingCompanyContext } from "../../../../providers";
import { useAuth } from "../../../../contexts/AuthProvider";
import { isMetaProvider, isBaileyProvider } from "../../../../contexts/AuthProvider/AuthProvider.helpers";
import { useTimezoneFormatter } from "../../../../hooks/useTimezoneFormatter";
import { useTableData } from "../../../../hooks/useTableData";
import { CustomDatePicker } from "../../../../components/formsComponents/CustomDatePicker";
import moment from "moment";
import { DataTableV2 } from "../../../../components/TableV2/DataTableV2";
import { useTableV2State } from "../../../../components/TableV2/hooks/useTableV2State";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../components/TableV2/interfaces/IDataTableV2Props";
import { TABLE_CACHING_KEYS } from "../../../../constants/constants";
import { IPaymentList } from "../../../../redux/paymentDetails/paymentDetails.types";
import { IndianPriceFormatter } from "../../../../helpers/formatWithCommas";
import { SendPaymentReminderDialog } from "../dialogs/SendPaymentReminderDialog";

const DATE_RANGE_OPTIONS = [
  { label: "1 Week", subtitle: "Booking Left", value: "7" },
  { label: "2 Weeks", subtitle: "Booking Left", value: "15" },
  { label: "1 Month", subtitle: "Booking Left", value: "30" },
  { label: "2 Months", subtitle: "Booking Left", value: "60" },
  { label: "3 Months", subtitle: "Booking Left", value: "90" },
  { label: "6 Months", subtitle: "Booking Left", value: "180" },
  { label: "Custom", subtitle: "Date Range", value: "custom" },
];

/**
 * Tab 3: Send Reminder Manually
 * Fetch pending payment leads (same data source as Payment Details) based on
 * function date range and send WhatsApp reminders on demand.
 * Uses DataTableV2 with selection and a "Send on WhatsApp" button.
 */
export const SendReminderManually: React.FC = () => {
  const { comapny } = useBillingCompanyContext();
  const { whatsappService } = useAuth();
  const { ftDate } = useTimezoneFormatter();

  const isWhatsAppActive = isMetaProvider(whatsappService) || isBaileyProvider(whatsappService);

  const [selectedFilter, setSelectedFilter] = useState<string>("7");
  const [customFromDate, setCustomFromDate] = useState<string>("");
  const [customToDate, setCustomToDate] = useState<string>("");
  const [selectedLeads, setSelectedLeads] = useState<IPaymentList[]>([]);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);

  // --- Table state ---
  const {
    state,
    setColumnVisibility,
    onPageChange,
    onRowsPerPageChange,
    onResizeMasterColumns,
    initialColumnVisibility,
  } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "allTimes",
      selectedTab: "-1",
    },
    pagination: {
      pageNumber: 1,
      rowsInPerPage: 25,
      apiFetchRowCount: 26,
      extraFetchFactor: 1,
    },
    cachingFilters: {
      uniqueKey: TABLE_CACHING_KEYS.PAYMENT_REMINDER_CAMPAIGN_LIST + "_MANUAL",
    },
  });

  const { columnsConfig, pagination, masterColumnResize } = state;

  /** Build advance filter for function_date based on selected option */
  const buildAdvanceFilter = () => {
    let fromDate = "";
    let toDate = "";

    if (selectedFilter === "custom") {
      fromDate = customFromDate;
      toDate = customToDate;
    } else {
      const today = moment();
      const days = parseInt(selectedFilter, 10);
      fromDate = today.format("YYYY-MM-DD");
      toDate = today.clone().add(days, "days").format("YYYY-MM-DD");
    }

    if (fromDate && toDate) {
      return [
        {
          column: ["function_date"],
          value: fromDate,
          logicalOperator: "AND" as const,
          operator: "GREATER_THAN_EQUAL" as const,
        },
        {
          column: ["function_date"],
          value: toDate,
          logicalOperator: "AND" as const,
          operator: "LESSER_THAN_EQUAL" as const,
        },
      ];
    }

    return undefined;
  };

  const advanceFilter = buildAdvanceFilter();

  const { data: list, loading, totalRecords, refetch } = useTableData<IPaymentList>({
    path: "/accounting/get-payment-dashboard",
    params: {
      company_outlet_uuid: comapny.company_outlet_uuid,
      advanceFilter,
      page: pagination.pageNumber,
      pageLimit: pagination.rowsInPerPage,
    },
  });

  const handleFilterChange = (value: string) => {
    setSelectedFilter(value);
    setSelectedLeads([]);
    if (value !== "custom") {
      setCustomFromDate("");
      setCustomToDate("");
    }
  };

  const handleSendComplete = () => {
    setSelectedLeads([]);
    refetch();
  };

  const tableProps: IDataTableV2Props = {
    mobileColumns: [
      {
        variant: "template1",
        headingColumn: {
          key: "name_of_guest",
          fieldName: "name_of_guest",
        },
        rightHeadingColumn: {
          key: "remaining_balance",
          fieldName: "remaining_balance",
          onRowCellRender: (_value, row: IPaymentList) => (
            <Typography variant="body2" color="error.main" fontWeight={600}>
              {IndianPriceFormatter(row.remaining_balance)}
            </Typography>
          ),
        },
        subSectionColumn: [
          {
            key: "contact_number_one",
            fieldName: "contact_number_one",
            onRowCellRender: (_value, row: IPaymentList) => (
              <Typography variant="body2">{row.contact_number_one || "—"}</Typography>
            ),
          },
        ],
        otherColumns: [
          {
            key: "preferred_venue",
            headerName: "Venue",
            fieldName: "preferred_venue",
          },
          {
            key: "event_type",
            headerName: "Event Type",
            fieldName: "event_type",
          },
          {
            key: "function_date",
            headerName: "Event Date",
            fieldName: "function_date",
            onRowCellRender: (_value, row: IPaymentList) =>
              row.function_date ? ftDate(row.function_date) : "—",
          },
          {
            key: "total_amount_paid",
            headerName: "Total Paid",
            fieldName: "total_amount_paid",
            onRowCellRender: (_value, row: IPaymentList) =>
              IndianPriceFormatter(row.total_amount_paid),
          },
        ],
      },
    ],
    isPagination: true,
    selectionMode: "multiple",
    uniqueRowKeyName: "leads_uuid",
    rowsPerPageOptions: pagination.rowsInPerPage,
    isDataLoading: loading,
    extraFetchFactor: pagination.extraFetchFactor,
    totalRecords: totalRecords,
    pageNumber: pagination.pageNumber,
    tableCommandBarProps: {
      preDefinedPlugins: {
        columnVisibility: {
          columnVisibility: columnsConfig.columnVisibility,
          initialColumnVisibility,
          onChange: setColumnVisibility,
        },
        refresh: { onClick: () => refetch() },
      },
      leftItems: { customPlugins: [] },
      rightItems: {
        customPlugins: [
          {
            key: "send-whatsapp",
            onRender: () => {
              if (selectedLeads.length === 0) return null;
              const isBaileyBlocked = isBaileyProvider(whatsappService) && selectedLeads.length > 15;
              const isDisabled = !isWhatsAppActive || isBaileyBlocked;
              const tooltipText = !isWhatsAppActive
                ? "WhatsApp service is not active"
                : isBaileyBlocked
                ? "Cannot send more than 15 messages via QR WhatsApp. Switch to Meta WhatsApp."
                : "";

              return (
                <Tooltip title={tooltipText}>
                  <span>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<Send />}
                      disabled={isDisabled}
                      onClick={() => setSendDialogOpen(true)}
                      sx={{ textTransform: "none" }}
                    >
                      {`Send on WhatsApp (${selectedLeads.length})`}
                    </Button>
                  </span>
                </Tooltip>
              );
            },
          },
        ],
      },
    },
    masterColumns: [
      {
        key: "name_of_guest",
        headerName: "Guest Name",
        fieldName: "name_of_guest",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
        enableSorting: true,
      },
      {
        key: "contact_number_one",
        headerName: "Contact Number",
        fieldName: "contact_number_one",
        renderType: DataTableV2RowRenderType.TEXT,
        enableSorting: true,
      },
      {
        key: "function_date",
        headerName: "Event Date",
        fieldName: "function_date",
        renderType: DataTableV2RowRenderType.DATE,
        enableSorting: true,
        onRowCellRender: (_value, row: IPaymentList) =>
          row.function_date ? ftDate(row.function_date) : "—",
      },
      {
        key: "event_type",
        headerName: "Event Type",
        fieldName: "event_type",
        renderType: DataTableV2RowRenderType.TEXT,
        enableSorting: true,
      },
      {
        key: "preferred_venue",
        headerName: "Venue",
        fieldName: "preferred_venue",
        renderType: DataTableV2RowRenderType.TEXT,
        enableSorting: true,
      },
      {
        key: "remaining_balance",
        headerName: "Remaining Balance",
        fieldName: "remaining_balance",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        enableSorting: true,
        onRowCellRender: (_value, row: IPaymentList) => (
          <Typography variant="body2" color="error.main" fontWeight={600}>
            {IndianPriceFormatter(row.remaining_balance)}
          </Typography>
        ),
      },
      {
        key: "total_amount_paid",
        headerName: "Total Paid",
        fieldName: "total_amount_paid",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        enableSorting: true,
        onRowCellRender: (_value, row: IPaymentList) => (
          <Typography variant="body2" color="success.main" fontWeight={500}>
            {IndianPriceFormatter(row.total_amount_paid)}
          </Typography>
        ),
      },
    ],
    rows: list,
    onPageChange,
    onRowsPerPageChange,
    onSelection: (_isAllSelected: boolean, rows: IPaymentList[]) => {
      setSelectedLeads(rows);
    },
    masterColumnResize: {
      values: masterColumnResize,
      onResize: onResizeMasterColumns,
    },
  };

  return (
    <Box>
      {/* Booking in: Card-style date range selector */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="nowrap" sx={{ overflowX: "auto", mb: 2, pb: 1 }}>
        {DATE_RANGE_OPTIONS.map((opt) => {
          const isActive = selectedFilter === opt.value;
          return (
            <Box
              key={opt.value}
              onClick={() => handleFilterChange(opt.value)}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: "12px",
                cursor: "pointer",
                minWidth: 120,
                flexShrink: 0,
                textAlign: "center",
                // border: isActive ? "1.5px solid" : "none",
                borderColor: isActive ? "primary.main" : "transparent",
                backgroundColor: isActive ? "primary.main" : "#fff",
                color: isActive ? "#fff" : "text.primary",
                boxShadow: isActive ? "none" : "0px 1px 4px rgba(0,0,0,0.12)",
                transition: "all 0.15s ease",
                "&:hover": {
                  backgroundColor: isActive ? "primary.dark" : "#fafafa",
                  boxShadow: isActive ? "none" : "0px 2px 6px rgba(0,0,0,0.15)",
                },
              }}
            >
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ color: isActive ? "#fff" : "primary.main", lineHeight: 1.3 }}
              >
                {opt.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isActive ? "rgba(255,255,255,0.7)" : "#888", fontSize: "0.7rem" }}
              >
                {opt.subtitle}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      {/* Custom date range pickers */}
      {selectedFilter === "custom" && (
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <CustomDatePicker
            placeholder="From Date"
            value={customFromDate || null}
            onChange={(date: any) => setCustomFromDate(date || "")}
          />
          <CustomDatePicker
            placeholder="To Date"
            value={customToDate || null}
            onChange={(date: any) => setCustomToDate(date || "")}
          />
        </Stack>
      )}

      {/* Bailey warning when >15 leads selected */}
      {isBaileyProvider(whatsappService) && selectedLeads.length > 15 && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            bgcolor: "#fef3cd",
            border: "1.5px solid #f0ad4e",
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="#664d03" fontWeight={600}>
            ⚠️ Sending to more than 15 contacts via QR WhatsApp is not recommended. There are chances of your number being blocked.
          </Typography>
          <Typography component="span" variant="body2" fontWeight={700} sx={{ color: "#25D366" }}>
            Consider using Meta WhatsApp instead.
          </Typography>
        </Box>
      )}

      <DataTableV2 {...tableProps} />

      <SendPaymentReminderDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        selectedLeads={selectedLeads}
        companyOutletUuid={comapny.company_outlet_uuid}
        companyOutletName={comapny.company_outlet_name}
        onSendComplete={handleSendComplete}
      />
    </Box>
  );
};
